/**
 * External Imports
 */
import {
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { connect, useDispatch } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuIcon } from '@neutron/react';

/**
 * Internal Imports
 */
import AttachedCard from '../attachedCard';

import { RootState } from '../../../../redux/store';
import {
  setModalType,
  setModalData,
  setModalAction,
  openModal,
  clearSearchStoplights,
  getStoplightById,
  searchStoplights,
  clearSearchIssues,
  saveTaskModalState,
  searchIssues
} from '../../../../redux/actions';
import { useLazyGetIssueByIdQuery } from '../../../../redux/apis/Tasks.api';

import { useDebounceValue } from '../../../../utils/debouncers';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Issue,
  Stoplight,
  Task,
  TaskPost
} from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  AttachedContainer,
  SearchInput,
  NoneAttachedLabel,
  ShowHideSpan,
  StyledDivider,
  ViewIssuesLabel
} from './SearchView.styles';

/**
 * Constant Imports
 */
import serviceCentralCategories from '../../../../constants/serviceCentralCategories.json';

const SearchView: FC<{
  attachedDisplay: (Issue & Stoplight)[];
  attachToItem: TaskPost & Stoplight;
  attachToItemCompleted?: boolean;
  authorizedUser: AuthUserProps;
  prevAttached: string[];
  searchedIssues: Issue[];
  searchedStoplights: Stoplight[];
  type: 'issue' | 'stoplight';
  handleRemoveAttached: (
    e: MouseEvent<HTMLNeuIconElement>,
    item: Issue & Stoplight
  ) => void;
  setItemsToAttach: (item: Issue & Stoplight) => void;
}> = ({
  attachedDisplay,
  attachToItem,
  attachToItemCompleted,
  authorizedUser,
  prevAttached,
  searchedIssues,
  searchedStoplights,
  type,
  handleRemoveAttached,
  setItemsToAttach
}) => {
  const dispatch = useDispatch();

  // search and attached states
  const [searchState, setSearchState] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentlyAttachedOpen, setCurrentlyAttachedOpen] = useState(true);
  const [newlyAttachedOpen, setNewlyAttachedOpen] = useState(true);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const dbSearchTerm = useDebounceValue(searchTerm, 250);

  const [
    getIssueById,
    {
      data: issueByIdData = {} as Task
      // isError: isIssueByIdError,
      // isFetching: isIssueByIdFetching,
      // isLoading: isIssueByIdLoading
    }
  ] = useLazyGetIssueByIdQuery();

  const searched = useMemo(
    () =>
      type === 'issue'
        ? searchedIssues.map((issue: Issue) => ({
            ...issue,
            id: issue.taskId
          }))
        : searchedStoplights,
    [searchedIssues, searchedStoplights]
  ) as (Issue & Stoplight)[];

  const handleOpenIssue = (id: string) => {
    // getting issue clicked upon to open
    dispatch(saveTaskModalState({ redirect: true }));
    if (id) getIssueById(id);
  };

  const handleSearching = (e: CustomEvent<any>) => {
    const target = e.target as HTMLInputElement;
    setSearchState(true);
    setSearchTerm(target.value);
  };

  // Open stoplight modal from issue panel
  const openStoplight = useCallback(() => {
    if (attachToItem.stoplightId) {
      dispatch(saveTaskModalState({ redirect: true }));
      dispatch(getStoplightById(attachToItem.stoplightId));
      dispatch(setModalType('Edit Stoplight'));
      dispatch(setModalAction('VIEW'));
      dispatch(openModal());
    }
  }, []);

  useEffect(() => {
    return type === 'issue'
      ? () => {
          dispatch(clearSearchIssues());
        }
      : () => {
          dispatch(clearSearchStoplights());
        };
  }, []);

  // watching for the issue by id then sending modalData
  useEffect(() => {
    if (Object.keys(issueByIdData).length > 0) {
      dispatch(setModalData(issueByIdData));
      const category = issueByIdData.category || issueByIdData.taskSubtype;
      if (category && serviceCentralCategories.includes(category)) {
        dispatch(setModalType('View Issue'));
      } else {
        dispatch(
          setModalType(issueByIdData.completed ? 'View Issue' : 'Edit Issue')
        );
      }
    }
  }, [issueByIdData]);

  // search
  useEffect(() => {
    if (dbSearchTerm.length > 1 && /^([0-9]{2,})+$/i.test(dbSearchTerm)) {
      if (type === 'issue') {
        const queryParams = {
          query: dbSearchTerm,
          origin: attachToItem.origin || '',
          page: 0
        };
        dispatch(searchIssues(queryParams));
      } else {
        const queryParams = {
          facilityId: authorizedUser.facilityId,
          query: dbSearchTerm,
          origin: attachToItem.roundingType,
          page: 0
        };
        dispatch(searchStoplights(queryParams));
      }
    } else {
      if (type === 'issue') {
        dispatch(clearSearchIssues());
      }
      if (type === 'stoplight') {
        dispatch(clearSearchStoplights());
      }
    }
  }, [dbSearchTerm, attachToItem.origin]);

  const filteredSearched = useMemo(
    () =>
      searched.filter(
        (item: Issue & Stoplight) =>
          !attachedDisplay.some(
            (attached: Issue & Stoplight) => attached.id === item.id
          )
      ),
    [attachedDisplay, searched]
  );

  return (
    <div id="modal-search" className="my-1 px-0" ref={scrollContainerRef}>
      {attachToItemCompleted && prevAttached.length === 0 ? (
        <NoneAttachedLabel className="mt-4">
          {type === 'issue' ? 'No Issues' : 'No Stoplight'}
        </NoneAttachedLabel>
      ) : (
        <>
          <div id="Modal-Search-Attach" className="mb-1 mb-2">
            {!attachToItemCompleted && (
              <SearchInput
                id="Modal-Search-Attach-Input"
                className="w-100"
                label={
                  //   // !stoplightObject.category
                  //   // ? 'Must select stoplight category' :
                  !attachToItemCompleted
                    ? type === 'issue'
                      ? 'Attach an Issue (Start typing ID)'
                      : 'Attach a Stoplight (Start typing ID)'
                    : ''
                }
                // disabled={!stoplightObject.category && !stoplightObject.category}
                name="issue"
                autocomplete="off"
                inputMode="search"
                autoCorrect="off"
                spellCheck
                value={searchState ? searchTerm : null}
                onNeuChange={searchState ? handleSearching : () => {}}
                onNeuFocus={() => {
                  setSearchState(true);
                  setSearchTerm('');
                }}
              />
            )}
          </div>
          <div className="p-0">
            {attachedDisplay.filter((item: Issue & Stoplight) =>
              prevAttached.some((id: string) => item.id === id)
            ).length > 0 && (
              <>
                <ViewIssuesLabel className="neu-text-blue-80 d-flex justify-content-between">
                  Currently Attached
                  <ShowHideSpan
                    className="d-flex align-items-center"
                    onClick={() =>
                      setCurrentlyAttachedOpen(!currentlyAttachedOpen)
                    }
                  >
                    {currentlyAttachedOpen ? 'Hide' : 'Show'}
                    <NeuIcon>
                      {currentlyAttachedOpen ? 'expand_less' : 'expand_more'}
                    </NeuIcon>
                  </ShowHideSpan>
                </ViewIssuesLabel>
                <StyledDivider
                  className="neu-text-gray-30 my-2"
                  style={{ width: '98%' }}
                />
              </>
            )}
            <AttachedContainer className="py-0 px-2">
              {currentlyAttachedOpen &&
                attachedDisplay
                  .filter((item: Issue & Stoplight) =>
                    prevAttached.some((id: string) => item.id === id)
                  )
                  .map((item: Issue & Stoplight) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <AttachedCard
                      attached
                      attachToItemCompleted={attachToItemCompleted}
                      item={item}
                      key={item.id}
                      scrollContainerRef={scrollContainerRef}
                      type={type}
                      handleOpen={
                        type === 'issue' ? handleOpenIssue : openStoplight
                      }
                      handleRemoveAttached={handleRemoveAttached}
                      serviceCentralCategories={serviceCentralCategories}
                      setItemsToAttach={setItemsToAttach}
                      setNewlyAttachedOpen={setNewlyAttachedOpen}
                    />
                  ))}
            </AttachedContainer>
            {!attachToItemCompleted &&
              attachedDisplay.filter(
                (item: Issue & Stoplight) =>
                  !prevAttached.some((id: string) => item.id === id)
              ).length > 0 && (
                <>
                  <ViewIssuesLabel className="neu-text-blue-80 d-flex justify-content-between mt-1">
                    Newly Attached
                    <ShowHideSpan
                      className="d-flex align-items-center"
                      onClick={() => setNewlyAttachedOpen(!newlyAttachedOpen)}
                    >
                      {newlyAttachedOpen ? 'Hide' : 'Show'}
                      <NeuIcon>
                        {newlyAttachedOpen ? 'expand_less' : 'expand_more'}
                      </NeuIcon>
                    </ShowHideSpan>
                  </ViewIssuesLabel>
                  <StyledDivider
                    className="neu-text-gray-30 my-3"
                    style={{ width: '96%' }}
                  />
                  <AttachedContainer className="py-0 px-2">
                    {newlyAttachedOpen &&
                      attachedDisplay
                        .filter(
                          (item: Issue & Stoplight) =>
                            !prevAttached.some((id: string) => item.id === id)
                        )
                        .map((item: Issue & Stoplight) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <AttachedCard
                            attached
                            attachToItemCompleted={attachToItemCompleted}
                            item={item}
                            key={item.id}
                            scrollContainerRef={scrollContainerRef}
                            type={type}
                            handleRemoveAttached={handleRemoveAttached}
                            serviceCentralCategories={serviceCentralCategories}
                            setItemsToAttach={setItemsToAttach}
                            setNewlyAttachedOpen={setNewlyAttachedOpen}
                          />
                        ))}
                  </AttachedContainer>
                </>
              )}
          </div>
          {filteredSearched.length > 0 && (
            <ViewIssuesLabel className="mt-1">
              {filteredSearched.length} Result
              {filteredSearched.length > 1 && 's'}
            </ViewIssuesLabel>
          )}
          <div className="py-0 px-2">
            {filteredSearched.map((item: Issue & Stoplight) => (
              <AttachedCard
                attachToItemCompleted={attachToItemCompleted}
                item={item}
                key={item.id}
                scrollContainerRef={scrollContainerRef}
                type={type}
                serviceCentralCategories={serviceCentralCategories}
                setItemsToAttach={setItemsToAttach}
                setNewlyAttachedOpen={setNewlyAttachedOpen}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  authorizedUser: state.AuthorizedUser.authorizedUser,
  searchedIssues: state.TaskReducer.searchedIssues,
  searchedStoplights: state.StoplightReducer.searchedStoplights
});

export default connect(mapReduxStateToProps)(SearchView);
